import { FC, useEffect, useState } from 'react';
import { CreateIcon, EditIcon } from 'assets/icons';
import Guard from 'features/Guard';
import { useAppDispatch, useAppSelector } from 'hooks';
import { useTranslation } from 'react-i18next';
import { activateDataByCatalogName, deactivateDataByCatalogName, getLookupByName, } from 'store';
import { createOrEditBtn } from 'styles/MUIStyles';

import Button from '@mui/material/Button';

import ConfirmationModal from '../../../features/ConfirmationModal';
import ListItemActions from '../../../features/ListItemActions';
import { SkillLevel } from '../../../models/catalogs';

import { SkillLevelForm } from './SkillLevelForm';

import styles from '../CatalogsLayout.module.scss';

export interface IFormInput {
    name: string;
    skillSetId: number | string;
    indexLevel: number;
    description: string;
}

const SkillLevels: FC<{
    name: string;
    createOrUpdate: null | string;
    setCreateOrUpdate: Function;
    fulfilledStatusHandler: Function;
}> = ({
    name,
    createOrUpdate,
    setCreateOrUpdate,
    fulfilledStatusHandler,
}): JSX.Element => {
        const dispatch = useAppDispatch();
        const { t } = useTranslation();

        const skillLevels = useAppSelector((state) => state.catalogs.skillLevels);
        const skillSets = useAppSelector((state) => state.lookups.skillSets);
        const allowedPermissions = useAppSelector(
            (state) => state.auth.allowedPermissions,
        );

        const [isDrawerOpen, setIsDrawerOpen] = useState(false);
        const [activeSkillLevel, setActiveSkillLevel] = useState<null | SkillLevel>(
            null,
        );

        const [confirmationModalOpen, setConfirmationModalOpen] = useState<{
            isOpen: boolean;
            actionName: string;
        }>({ isOpen: false, actionName: '' });

        const activateItem = async () => {
            if (activeSkillLevel?.id) {
                const response = await dispatch(
                    activateDataByCatalogName({
                        catalogName: name,
                        catalogNameId: activeSkillLevel?.id,
                    }),
                );
                if (response.meta.requestStatus === 'fulfilled') {
                    fulfilledStatusHandler('notifications.successfullyActivated');
                    setConfirmationModalOpen({ isOpen: false, actionName: '' });
                }
                return;
            }
        };

        const deactivateItem = async () => {
            if (activeSkillLevel?.id) {
                const response = await dispatch(
                    deactivateDataByCatalogName({
                        catalogName: name,
                        catalogNameId: activeSkillLevel?.id,
                    }),
                );

                if (
                    response.meta.requestStatus === 'fulfilled' &&
                    response.payload
                ) {
                    fulfilledStatusHandler(
                        t('notifications.recordInUseNotice'),
                        'warn',
                    );
                    setConfirmationModalOpen({ isOpen: false, actionName: '' });
                    return;
                }

                if (response.meta.requestStatus === 'fulfilled') {
                    fulfilledStatusHandler('notifications.successfullyDeactivated');
                    setConfirmationModalOpen({ isOpen: false, actionName: '' });
                }
            }
        };

        const onCreateItem = () => {
            setIsDrawerOpen(true);
            setCreateOrUpdate('create');
            setActiveSkillLevel(null);
        };

        const onEditItem = () => {
            setIsDrawerOpen(true);
            setCreateOrUpdate('update');
        };

        const confirmActionHandler = () => {
            switch (confirmationModalOpen.actionName) {
                case 'Activate':
                    activateItem();
                    break;
                case 'Deactivate':
                    deactivateItem();
                    break;
            }
        };

        // const closeFormDrawer = () => {
        //     setCreateOrUpdate(null);
        //     setIsDrawerOpen(false);
        //     dispatch(clearCatalogTextFieldErrors());
        //     // clearErrors();
        // };

        useEffect(() => {
            setActiveSkillLevel(null);
            dispatch(getLookupByName('skillset'));
        }, [dispatch, name]);

        return (
            <div>
                <div className={styles.crudButtonWrapper}>
                    <Guard
                        allowedPermissions={allowedPermissions}
                        permissionName="Create"
                    >
                        <Button
                            startIcon={<CreateIcon />}
                            variant="text"
                            disabled={false}
                            onClick={() => onCreateItem()}
                            sx={createOrEditBtn}
                        >
                            {t('buttons.Create')}
                        </Button>
                    </Guard>
                    <Guard
                        allowedPermissions={allowedPermissions}
                        permissionName="Edit"
                    >
                        <Button
                            startIcon={<EditIcon />}
                            variant="text"
                            disabled={!activeSkillLevel?.isActive}
                            onClick={() => onEditItem()}
                            sx={createOrEditBtn}
                        >
                            {t('buttons.Edit')}
                        </Button>
                    </Guard>
                </div>
                <div className={styles.tableHeader}>
                    <span className={styles.column}>{t('data.name')}</span>
                    <span className={styles.column}>{t('data.level')}</span>
                    <span className={styles.column}>{t('data.description')}</span>
                    <span className={styles.column}>{t('data.skillSet')}</span>
                    <span className={styles.column}>{t('data.actions')}</span>
                </div>
                <ul className={styles.catalogNameDataList}>
                    {skillLevels.map((item) => (
                        <li
                            key={item.id}
                            className={
                                activeSkillLevel?.id === item.id
                                    ? `${styles.catalogNameDataListItem} ${styles.catalogNameDataListItemActive}`
                                    : `${styles.catalogNameDataListItem}`
                            }
                            onClick={() =>
                                setActiveSkillLevel({
                                    id: item.id,
                                    name: item.name,
                                    description: item.description,
                                    isActive: item.isActive,
                                    skillSetId: item.skillSetId,
                                    indexLevel: item.indexLevel,
                                })
                            }
                        >
                            <span className={styles.itemDescription}>{item.name}</span>
                            <span className={styles.itemDescription}>{item.indexLevel}</span>
                            <span className={styles.itemDescription}>{item.description}</span>
                            <span className={styles.itemDescription}>
                                {
                                    skillSets?.find(
                                        (el) => el.id === item.skillSetId,
                                    )?.name
                                }
                            </span>
                            <span>
                                <ListItemActions
                                    itemId={item.id}
                                    isItemActive={item.isActive}
                                    activationAvailable={true}
                                    setConfirmationModalOpen={
                                        setConfirmationModalOpen
                                    }
                                    editItemClick={onEditItem}
                                />
                            </span>
                        </li>
                    ))}
                </ul>

                <SkillLevelForm
                    name={name}
                    activeSkillLevel={activeSkillLevel}
                    setActiveSkillLevel={setActiveSkillLevel}
                    createOrUpdate={createOrUpdate}
                    setCreateOrUpdate={setCreateOrUpdate}
                    isDrawerOpen={isDrawerOpen}
                    setIsDrawerOpen={setIsDrawerOpen}
                    fulfilledStatusHandler={fulfilledStatusHandler}
                />

                <ConfirmationModal
                    isModalOpen={confirmationModalOpen.isOpen}
                    setIsModalOpen={setConfirmationModalOpen}
                    actionName={confirmationModalOpen.actionName}
                    instanceName={name}
                    handleAction={confirmActionHandler}
                />
            </div>
        );
    };

export default SkillLevels;
